import { createApp, watch } from 'vue'
import '@sennder/design-system-core'
import { loadStyles, themeProvider, Themes } from '@sennder/design-system-core'
import { envOverrides, injectRuntimeConfig } from '@sennder/shell-utilities'
import { Shell } from '@sennder/microfrontend-registry'
import { SharedDataType } from '@sennder/senn-node-microfrontend-interfaces'

import '@/assets/tailwind.global.css'
import '@sennder/shell-utilities/dist/style.css'

import App from '@/App.vue'
import router from '@/router'
import {
  ENV,
  LD_KEY,
  isLocalEnv,
  FRONTEND_METRICS_API_URL,
  isProductionEnv,
  isUatEnv,
} from '@/common/config'
import { registerPlugins } from '@/plugins'
import { translationProvider } from '@/services/translationProvider'
import { loadUserData } from '@/store'
import { logger } from '@/services/logger/loggers'
import { registerModalManager } from '@/components/modal-manager'
import { getCookieConsent } from '@/services/cookies'
import { startTracking } from '@/services/tracking'
import { featureFlag } from '@/services/featureFlagProvider'
import { carrierProfilePublicService } from '@/services/carrierProfilePublicService'
import { addAppRoute, initRouter } from './router/routes'
import { getRegistryMfContext, shell, shellLogLevel } from './config/registry'
import { getStateData } from './store/getters'
import {
  initSessionReplay,
  stopSessionReplay,
  toggleEngagementSurvey,
} from '@/services/analytics'
import { setStoreLegacyFlags } from '@/store/setters'
import { getRenderer } from '@/config/routes'
import { navigation } from '@/config/navigation'
import { loggingOut } from '@/store/logoutActions'
import { TranslationInterceptor } from '@sennder/shell-utilities'
import { getAuth0Header } from '@/services/tokenManager'

let interceptor: TranslationInterceptor | undefined

if (FRONTEND_METRICS_API_URL) {
  interceptor = new TranslationInterceptor(
    FRONTEND_METRICS_API_URL,
    getAuth0Header,
    logger
  )
}

async function initialize() {
  window.onerror = function (message, url, line, column, error) {
    logger.error('[Orcas shell - global error]', { error })
  }

  carrierProfilePublicService.cleanupLocalstorage()

  shell.value = new Shell(
    SharedDataType.ORCAS,
    getRegistryMfContext,
    ENV,
    logger,
    'infinite',
    shellLogLevel
  )

  // don't load data and mount the app until user gives cookie consent
  await checkCookieConsent()

  if (!LD_KEY) {
    throw new Error('LD_KEY not found')
  }

  if (isLocalEnv()) {
    envOverrides.load(logger)
    injectRuntimeConfig(shell.value, addAppRoute, navigation, getRenderer)
  }

  registerModalManager()

  await Promise.all([
    featureFlag.init(LD_KEY, undefined, undefined, { timeout: 10 }),
    loadUserData(),
    translationProvider.initialize(),
  ])
}

async function checkCookieConsent() {
  try {
    const consent = await getCookieConsent()
    if (consent.ANALYTICS) {
      startTracking()
    }
    return consent
  } catch (error) {
    logger.error(
      `[orcas-shell - checkCookieConsent] Error checking cookie consent`,
      { error }
    )
  }
}

async function start() {
  await loadStyles('body')
  themeProvider('html', Themes.Light)

  try {
    await initialize()
  } catch (error) {
    logger.error(`Orcas shell init`, { error })
  } finally {
    const app = createApp(App)
    const { unmount } = app

    const stopWatchData = watch(getStateData, () => {
      if (!loggingOut) {
        shell.value?.onSharedDataChange()
      }
    })

    // This should be the only watchAllFlags call in the application to avoid noisy logging to LaunchDarkly. Otherwise, each watchAllFlags will report another set of feature flag evaluations.
    const stopWatchLegacyFlags =
      featureFlag.devFunctions.watchAllFlags(setStoreLegacyFlags)

    const stopWatchInterceptorFlag = featureFlag.watchFeatureFlag(
      'TRACK_TRANSLATION_USAGE',
      (enabled) => {
        if (!isUatEnv() && !isProductionEnv()) {
          if (enabled) {
            interceptor?.enable()
            // Re-initialize to make sure shell translations are properly intercepted
            translationProvider.initialize()
          } else {
            interceptor?.disable()
          }
        }
      }
    )

    const stopWatchAmplitudeReplayFlag = featureFlag.watchFeatureFlag(
      'ENABLE_AMPLITUDE_SESSION_REPLAY',
      (enable: boolean) => {
        if (enable) {
          initSessionReplay()
        } else {
          stopSessionReplay()
        }
      }
    )

    const stopWatchAmplitudeEngagementSurveyFlag = featureFlag.watchFeatureFlag(
      'ENABLE_AMPLITUDE_ENGAGEMENT_SURVEY',
      toggleEngagementSurvey
    )

    app.unmount = (...args) => {
      unmount(...args)
      stopWatchData()
      stopWatchLegacyFlags()
      stopWatchAmplitudeReplayFlag()
      stopWatchAmplitudeEngagementSurveyFlag()
      stopWatchInterceptorFlag()
      interceptor?.disable()
      shell.value?.destroy()
    }

    await initRouter()
    app.use(router)

    registerPlugins(app)

    app.mount('#app')
  }
}

start()
